import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

export default function MsaTaaPage({ project, arps, obstacles, loading, state, setState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const { getStyle } = useSurfaceStyles();
    const msaStyle = getStyle('msa.boundary');
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate an MSA/TAA sector, not just its tallest point overall.
    const msaResultsForShield = state?.msaResults || {};
    const penetratingObstacleIds = useMemo(() => {
        const ids = [];
        Object.values(msaResultsForShield).forEach(entry => {
            (entry?.data?.results || []).forEach(r => { if (r.penetrates) ids.push(r.obstacle_id); });
        });
        return ids;
    }, [msaResultsForShield]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const tileLyr = useRef(null);
    const mkrLyrs = useRef([]);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [exportingAixm, setExportingAixm] = useState(false);
    const [lowestMsaTaaMap, setLowestMsaTaaMap] = useState({});
    const [capturing, setCapturing] = useState(false);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(400, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);
    
    // Helpers to update complex state prop
    const setSelId = useCallback((id) => setState(s => ({ ...s, selObstId: id })), [setState]);
    const setVisible = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.visible) : v;
        return { ...s, visible: next };
    }), [setState]);
    const setMsaResults = useCallback((v) => setState(s => {
        const next = typeof v === 'function' ? v(s.msaResults) : v;
        return { ...s, msaResults: next };
    }), [setState]);

    const { selObstId, visible, msaResults } = state;
    const lastFittedObsId = useRef(null);

    const obsWithCoords = React.useMemo(
        () => obstacles.filter(o => o.latitude && o.longitude),
        [obstacles]
    );
    const selectedObs = obsWithCoords.find(o => o.id === selObstId) || obsWithCoords[0];

    useEffect(() => {
        fetch(`${API_BASE}/pans/lowest-msa-taa`)
            .then(res => res.json())
            .then(data => setLowestMsaTaaMap(data || {}))
            .catch(err => console.error("Failed to fetch MSA TAA map", err));
    }, []);

    const validArps = React.useMemo(() => {
        const NM30 = 55.56; // 30 nautical miles in km
        return arps.filter(a => 
            lowestMsaTaaMap.hasOwnProperty(a.icao?.toUpperCase()) && 
            (a.distance_km == null || a.distance_km <= NM30)
        );
    }, [arps, lowestMsaTaaMap]);

    useEffect(() => {
        if (!validArps.length) return;
        const vis = {};
        validArps.forEach(a => { vis[a.icao] = true; });
        setVisible(vis);
    }, [validArps, setVisible]);

    // Map init
    useEffect(() => {
        if (mapInst.current || !mapRef.current) return;
        mapInst.current = L.map(mapRef.current, {
            center: [-28.5, 25.0],
            zoom: 6,
            zoomDelta: 0.7,
            zoomSnap: 0.7
        });
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        setTimeout(() => { mapInst.current?.invalidateSize(); }, 0);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
    }, []);

    // Toggle base layers
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileLyr.current) mapInst.current.removeLayer(tileLyr.current);
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // Render features
    useEffect(() => {
        if (!mapInst.current || loading) return;
        const map = mapInst.current;
        mkrLyrs.current.forEach(layer => map.removeLayer(layer));
        mkrLyrs.current = [];

        const bounds = [];

        // Draw MSA surfaces — real sector polygons when this aerodrome's
        // navaid has one uploaded (backend/MSA/), otherwise the generic
        // 30NM circle fallback.
        validArps.forEach(arp => {
            if (visible[arp.icao] === false) return;
            const resData = msaResults[arp.icao]?.data;
            const isReal = resData?.source === 'real' && resData.sectors?.length;

            if (arp.lat && arp.lon) {
                const pt = [arp.lat, arp.lon];
                bounds.push(pt);

                const navaidLine = isReal ? `<br/>Navaid: ${resData.navaid} (real MSA sectors)` : '';
                const m = L.marker(pt, { icon: getArpIcon(L, arp, showArpLabels) })
                    .bindTooltip(`<b>${arp.icao}</b><br/>Lowest MSA: ${lowestMsaTaaMap[arp.icao]} ft${navaidLine}`, { direction: 'top' });
                if (onAerodromeContextMenu) {
                    m.on('contextmenu', e => {
                        e.originalEvent.preventDefault();
                        onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                    });
                }
                m.addTo(map);
                mkrLyrs.current.push(m);

                if (isReal) {
                    resData.sectors.forEach(s => {
                        const latlngs = s.ring.map(([lat, lon]) => [lat, lon]);
                        bounds.push(...latlngs);
                        const poly = L.polygon(latlngs, {
                            color: msaStyle.color, weight: 1.5,
                            fillColor: msaStyle.color, fillOpacity: msaStyle.opacity,
                        }).bindTooltip(`${arp.icao} – Sector ${s.sector}${s.alt_ft != null ? ` — ${s.alt_ft} ft` : ' — no published altitude'}`);
                        poly.addTo(map);
                        mkrLyrs.current.push(poly);
                    });
                } else {
                    // 30NM radius (55560 meters) — generic fallback
                    const msaCircle = L.circle(pt, {
                        radius: 55560,
                        color: msaStyle.color, weight: 1.5,
                        fillColor: msaStyle.color, fillOpacity: msaStyle.opacity,
                        dashArray: '5, 5'
                    }).bindTooltip(`${arp.icao} – MSA 30NM Boundary`);
                    msaCircle.addTo(map);
                    mkrLyrs.current.push(msaCircle);
                }
            }
        });

        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude]; bounds.push(ll);
            const isSel = o.id === selectedObs?.id;

            let isPenetrating = false;
            let isAssessed = false;
            let maxPen = -Infinity;
            let foundAny = false;
            let surfaceStr = '—';

            for (const arp of validArps) {
                if (visible[arp.icao] === false) continue;
                if (msaResults[arp.icao] && !msaResults[arp.icao].loading) isAssessed = true;
                const resData = msaResults[arp.icao]?.data;
                if (resData && resData.results) {
                    const obstacleResult = resData.results.find(r => r.obstacle_id === o.id);
                    if (obstacleResult) {
                        foundAny = true;
                        if (obstacleResult.penetrates) isPenetrating = true;
                        // Real sector data has no single taa_msa (null) — each
                        // result row carries its own matched sector's altitude instead.
                        const refAlt = obstacleResult.sector_alt_ft ?? resData.taa_msa ?? 0;
                        const totalElevFt = obstacleResult.total_elev_ft || 0;
                        const pen = (totalElevFt - refAlt) * 0.3048;
                        if (pen > maxPen) {
                            maxPen = pen;
                            surfaceStr = obstacleResult.sector
                                ? `MSA ${obstacleResult.sector} (${arp.icao})`
                                : `MSA Sector/Area (${arp.icao})`;
                        }
                    }
                }
            }

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const icon = getObstacleIcon(L, o, isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined, showObsLabels);
            const m = L.marker(ll, { icon, zIndexOffset: isSel ? 100 : 0 })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`)
                .on('click', () => setSelId(o.id));
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(map);
            mkrLyrs.current.push(m);
        });
        addHullPolygonsToMap(map, obsWithCoords, project?.id, mkrLyrs);

        if (bounds.length > 0) {
            if (lastFittedObsId.current !== selectedObs?.id) {
                map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 10 });
                lastFittedObsId.current = selectedObs?.id;
            }
        }
    }, [validArps, obstacles, loading, msaResults, visible, lowestMsaTaaMap, showObstacles, showObsLabels, showArpLabels, selectedObs, onObstacleContextMenu, onAerodromeContextMenu, msaStyle]);

    // Fetch MSA assessments. Guard is a plain "already attempted?" check —
    // covers in-flight (loading: true) and failed (error) entries too, not
    // just successful ones. Without that, this effect re-running (e.g. once
    // obsWithCoords/validArps settle) would re-fire a fetch for anything
    // still loading, and since each fetch's own setMsaResults triggers a
    // re-render, that becomes a self-sustaining request storm — this is
    // what caused the reported ERR_INSUFFICIENT_RESOURCES loop.
    useEffect(() => {
        if (!obsWithCoords.length) return;
        validArps.forEach(arp => {
            if (visible[arp.icao] === false) return;
            if (msaResults[arp.icao]) return;

            setMsaResults(prev => ({ ...prev, [arp.icao]: { loading: true } }));
            fetch(`${API_BASE}/msa/assess`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    aerodrome: arp.icao,
                    obstacles: obsWithCoords
                })
            })
                .then(res => res.json())
                .then(data => {
                    setMsaResults(prev => ({ ...prev, [arp.icao]: { loading: false, data } }));
                })
                .catch(err => {
                    setMsaResults(prev => ({ ...prev, [arp.icao]: { loading: false, error: err.message } }));
                });
        });
    }, [visible, obsWithCoords, validArps]);

    const handleCapture = async () => {
        if (!mapInst.current || !project?.id) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch {}
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            await fetch(`${API_BASE}/msa/screenshot?project_id=${project.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: dataUrl }),
            });
        } catch (err) {
            console.error('Capture failed:', err);
        } finally {
            setCapturing(false);
        }
    };

    const renderResultsContent = () => {
        if (Object.entries(visible).filter(([_, isVis]) => isVis).length === 0) {
            return (
                <div style={{ padding: 30, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                    Select an aerodrome to view the MSA report.
                </div>
            );
        }
        return Object.entries(visible).filter(([_, isVis]) => isVis).map(([icao]) => {
            const res = msaResults[icao];
            if (!res) return null;

            // Mark the controlling obstacle — the one with the highest total
            // elevation, since a single lowest-MSA value applies to all of them.
            if (res.data?.results?.length) {
                const worst = res.data.results.reduce((a, b) => (b.total_elev_ft > a.total_elev_ft ? b : a));
                worst.isControlling = true;
            }

            const isReal = res.data?.source === 'real';

            return (
                <div key={icao} style={{ padding: 20, borderBottom: `1px dashed ${T.border}`, color: T.white }}>
                    {res.loading ? (
                        <div style={{ color: T.muted }}>Assessing {icao}...</div>
                    ) : res.error ? (
                        <div style={{ color: '#e84040' }}>Error evaluating {icao}: {res.error}</div>
                    ) : res.data && res.data.results ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div style={{ fontSize: 16, fontWeight: 'bold', color: '#3498db' }}>
                                    {res.data.aerodrome} MSA Assessment
                                    {isReal && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#2ec98b', border: '1px solid #2ec98b55', borderRadius: 4, padding: '2px 6px' }}>REAL MSA — {res.data.navaid}</span>}
                                </div>
                                <button
                                    onClick={() => {
                                        const headers = isReal
                                            ? ["Obstacle", "Sector", "Sector Alt (ft AMSL)", "Obst Elev (m)", "Obst Elev + 450m MOC (ft)", "Bearing (° T)", "Distance (NM)", "Result", "Controlling"]
                                            : ["Obstacle", "Lowest MSA (ft AMSL)", "Obst Elev (m)", "Obst Elev + 450m MOC (ft)", "Bearing (° T)", "Distance (NM)", "Result", "Controlling"];
                                        const rows = res.data.results.map(r => isReal
                                            ? [r.obstacle_name, r.sector, r.sector_alt_ft, r.obstacle_elev_m.toFixed(1), r.total_elev_ft, r.bearing, r.dist_nm, r.penetrates ? "IMPACT" : "NO IMPACT", r.isControlling ? 'Yes' : '']
                                            : [r.obstacle_name, res.data.taa_msa, r.obstacle_elev_m.toFixed(1), r.total_elev_ft, r.bearing, r.dist_nm, r.penetrates ? "IMPACT" : "NO IMPACT", r.isControlling ? 'Yes' : '']
                                        );
                                        exportToCsv(headers, rows, `${res.data.aerodrome}_MSA_Assessment.csv`);
                                    }}
                                    style={{
                                        padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                        background: T.blue, color: T.white, fontSize: 11, fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: 4
                                    }}
                                >
                                    📥 Export CSV
                                </button>
                            </div>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: T.bg0 }}>
                                <thead>
                                    <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Aerodrome</th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>ICAO DESIG</th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Navaid</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ borderBottom: `1px solid ${T.border}33` }}>
                                        <td style={{ padding: 10 }}>{validArps.find(a=>a.icao===icao)?.name || icao}</td>
                                        <td style={{ padding: 10, fontWeight: 'bold' }}>{res.data.aerodrome}</td>
                                        <td style={{ padding: 10, fontWeight: 'bold' }}>{res.data.navaid} ({res.data.navaid_type})</td>
                                    </tr>
                                </tbody>
                            </table>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                                <thead>
                                    <tr style={{ background: T.bg0, borderBottom: `2px solid ${T.border}` }}>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Obstacle</th>
                                        {isReal && <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Sector</th>}
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>{isReal ? 'Sector Alt' : 'Lowest MSA'}<br/><span style={{fontSize: 10, fontWeight: 'normal'}}>ft AMSL</span></th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Obst Elev<br/><span style={{fontSize: 10, fontWeight: 'normal'}}>In m</span></th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Obst Elev + 450m MOC<br/><span style={{fontSize: 10, fontWeight: 'normal'}}>Round up to next 100 ft (in ft AMSL)</span></th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Bearing (° T)</th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Distance (NM)</th>
                                        <th style={{ padding: 10, textAlign: 'left', color: T.muted }}>Result</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {res.data.results.map((r, idx) => (
                                        <tr key={idx} style={{ borderBottom: `1px solid ${T.border}33` }}>
                                            <td style={{ padding: 10, fontWeight: 'bold' }}>
                                                {r.obstacle_name} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                            </td>
                                            {isReal && <td style={{ padding: 10 }}>{r.sector}</td>}
                                            <td style={{ padding: 10, fontWeight: 'bold' }}>{isReal ? r.sector_alt_ft : res.data.taa_msa}</td>
                                            <td style={{ padding: 10 }}>{r.obstacle_elev_m.toFixed(1)}</td>
                                            <td style={{ padding: 10 }}>{r.total_elev_ft}</td>
                                            <td style={{ padding: 10 }}>{r.bearing}</td>
                                            <td style={{ padding: 10 }}>{r.dist_nm}</td>
                                            <td style={{ padding: 10, fontWeight: 'bold', color: r.penetrates ? '#e84040' : '#2ec98b' }}>
                                                {r.penetrates ? "IMPACT" : "NO IMPACT"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div style={{ marginTop: 16, padding: 16, background: T.bg0, borderRadius: 6, borderLeft: `4px solid ${res.data.is_penetration ? '#e84040' : '#2ec98b'}` }}>
                                <div style={{ marginBottom: 12, lineHeight: 1.5, fontSize: 13 }}>
                                    {res.data.is_penetration 
                                        ? "One or more obstacles exceed the lowest MSA. Further assessment is required."
                                        : "All evaluated obstacles are below the MSA threshold."
                                    }
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ color: T.muted }}>No obstacles fall within 30 NM of {icao}.</div>
                    )}
                </div>
            );
        });
    };

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=msa`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Server resolves the real aerodrome(s) from obstacle proximity and
            // builds the filename itself — read it back rather than
            // reconstructing one from project.aerodrome (can be stale).
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_MSA_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    return (
        <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <div style={{ width: 280, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Target Obstacle
                        </div>
                        <select
                            value={selObstId || ''}
                            onChange={(e) => setSelId(e.target.value)}
                            style={{
                                width: '100%', background: T.bg0, border: `1px solid ${T.border}`,
                                color: T.white, padding: '6px 8px', borderRadius: 4, fontSize: 13, outline: 'none'
                            }}
                        >
                            {obsWithCoords.map(o => (
                                <option key={o.id} value={o.id}>{o.name || 'Unnamed'}</option>
                            ))}
                        </select>
                        <button
                            onClick={handleExportAixm}
                            disabled={exportingAixm}
                            style={{ marginTop: 10, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                            title="Export obstacles, aerodrome data, waypoints and MSA areas (SafeAltitudeArea) as AIXM 5.1"
                        >
                            {exportingAixm ? '⏳…' : '📤 AIXM'}
                        </button>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <div style={{ padding: '12px 16px', fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', background: `rgba(255,255,255,0.02)` }}>
                            Aerodromes (within 30nm)
                        </div>
                        {validArps.length === 0 && (
                            <div style={{ padding: 16, color: T.muted, fontSize: 12, fontStyle: 'italic' }}>No affected MSA aerodromes within 30NM.</div>
                        )}
                        {validArps.map((a, i) => {
                            const isOn = visible[a.icao] !== false;

                            return (
                                <div key={i} style={{ borderBottom: `1px solid ${T.border}11`, paddingBottom: 6 }}>
                                    <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                        <input type="checkbox" checked={isOn} onChange={() => setVisible(v => ({ ...v, [a.icao]: !isOn }))} style={{ marginTop: 2, cursor: 'pointer', accentColor: T.blue }} />
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: isOn ? T.white : T.muted }}>
                                                {a.icao}
                                            </div>
                                            <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>
                                                {a.name}
                                            </div>
                                            {isOn && (
                                                <div style={{ padding: '4px 0px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3498db' }} />
                                                    <span style={{ fontSize: 10, color: T.white }}>Lowest MSA: {lowestMsaTaaMap[a.icao]} ft</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, position: 'relative', background: '#0a192f' }}>
                        {loading && <div style={{ position: 'absolute', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.5)', color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>Loading Map…</div>}
                        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', alignItems: 'center' }}>
                            {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                                <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                            ))}
                            <div style={{ width: 1, background: T.border, height: 16, margin: '0 4px' }} />
                            <button 
                                onClick={handleCapture} 
                                disabled={capturing}
                                style={{ 
                                    padding: '4px 12px', borderRadius: 6, border: 'none', cursor: capturing ? 'not-allowed' : 'pointer', 
                                    background: T.safe, color: T.white, fontSize: 11, fontWeight: 700,
                                    display: 'flex', alignItems: 'center', gap: 6
                                }}
                            >
                                {capturing ? '⌛ Saving...' : 'Capture Screenshot'}
                            </button>
                        </div>
                        <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv} />
                        <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : 400} />
                        <MapCoordinateViewer map={mapState} />
                        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                    </div>

                    <div style={{ height: resultsCollapsed ? 38 : resultsHeight, background: T.bg1, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden' }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🏔</span> MSA Report
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={() => {
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        fontWeight: 600
                                    }}
                                >
                                    {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                                </button>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out. 
                                    <button 
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : renderResultsContent()}
                        </div>
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - MSA Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>
    );
}
